import React from 'react'
import Turbolinks from 'turbolinks'
import PropTypes from 'prop-types'
import { helpers, urlHelpers } from '../../helpers'
import { Price, Rating } from '../core'
import { PlusLogo } from '../core/icons'

/**
* Displays a detailed product card.
*/
class DetailedProductCard extends React.Component {
  constructor(props) {
    super(props)

    this.getProductLink = this.getProductLink.bind(this)
    this.onClickHandler = this.onClickHandler.bind(this)

    this.renderPlaceholder = this.renderPlaceholder.bind(this)
    this.renderProductImage = this.renderProductImage.bind(this)
    this.renderCallout = this.renderCallout.bind(this)
  }

  /**
  * Returns the link to the product. Returns the empty string if the product
  * doesn't have a slug.
  *
  * @returns {string}
  */
  getProductLink() {
    if (!this.props.product.slug) {
      return ''
    }
    const ref = this.props.listName ? `${this.props.listName}-${this.props.position}` : `detailed-card-${this.props.position}`

    return urlHelpers.addRef(`/p/${this.props.product.slug}`, ref)
  }

  /**
  * On click event handler for when a user clicks the prooduct card. Tracks
  * the product as having been clicked and then takes the user to the product's
  * destination.
  */
  onClickHandler(e) {
    e.preventDefault()
    let price = null


    let sku = null

    // Parse the price of the variant.
    if (this.props.product.variant && this.props.product.variant.price) {
      price = this.props.product.variant.price
    }

    // Parse the sku of the variant.
    if (this.props.product.variant && this.props.product.variant.sku) {
      sku = this.props.product.variant.sku
    } else {
      sku = this.props.product.sku || null
    }
    $.track('product click', {
      id: sku,
      name: this.props.product.name,
      brand: helpers.formatProducers(this.props.product.producers),
      category: this.props.product.variant ? this.props.product.variant.oracle_department_name : null,
      variant: this.props.product.variant ? this.props.product.variant.options_text_analytics : null,
      position: this.props.position,
      price,
    }, this.props.listName, () => {
      Turbolinks.visit(this.getProductLink())
    })
  }

  /**
  * Renders a placeholder of a detailed product card.
  */
  renderPlaceholder() {
    return (
      <a className="detailed-product-card is-placeholder">
        <figure />
        <div className="product-card-content">
          <div className="price" />
          <div className="product-details">
            <div className="product-name" />
            <div className="producer" />
          </div>
          <div className="details">
            <Rating average="3.5" />
          </div>
        </div>
      </a>
    )
  }

  /**
  * Renders a special callout message for the product.
  */
  renderCallout() {
    let calloutText; let calloutClass; const
      styles = {}

    if (!this.props.product.customCallout && !this.props.product.calloutLabel) {
      return false
    }

    // Custom Callouts
    if (this.props.product.customCallout) {
      calloutText = this.props.product.customCallout

      calloutClass = 'is-custom'

      if (this.props.product.customCalloutTextColor) {
        styles.color = `#${this.props.product.customCalloutTextColor}`
      }

      if (this.props.product.customCalloutBackgroundColor) {
        styles.backgroundColor = `#${this.props.product.customCalloutBackgroundColor}`
        calloutClass += ' has-custom-background'
      }
    // Standard Callouts
    } else {
      calloutClass = `is-${this.props.product.calloutLabel}`

      switch (this.props.product.calloutLabel) {
        case 'sale':
          calloutText = 'On Sale Now'
          break
        case 'exclusive':
          calloutText = 'Deseret Book Exclusive'
          break
        case 'preorder':
          calloutText = 'Pre-order Now'
          break
      }
    }

    return (
      <div className={`card-callout ${calloutClass}`} style={styles}>
        { calloutText }
      </div>
    )
  }

  /**
  * Renders the product image.
  */
  renderProductImage() {
    // For the image use either the override image or the default display image.
    let image = {}

    if (this.props.product.image) {
      image = this.props.product.image
    } else if (this.props.product.display_image) {
      image = {
        alt: this.props.product.display_image.alt,
        url: this.props.product.display_image.attachment,
      }
    } else if (this.props.product.display_image_attachments) {
      image = {
        url: this.props.product.display_image_attachments.detail.url,
      }
    } else if (this.props.product.display_image_url) {
      image = {
        url: this.props.product.display_image_url,
      }
    }

    return (
      <figure data-hover-text="Learn More" className="can-override-before-background">
        { this.props.loading ? false : (
          <img
            data-original={image.url || ''}
            data-fallback-text={this.props.product.name || ''}
            data-position={this.props.position}
            alt={image.alt || this.props.product.name || ''}
            className="is-lazy fade-in has-drop-shadow"
          />
        ) }
      </figure>
    )
  }

  render() {
    if (this.props.loading) {
      return this.renderPlaceholder()
    }
    const classes = [
      'detailed-product-card',
      'is-loaded',
      this.props.isDisabled ? 'is-disabled' : '',
    ]

    return (
      <a
        href={this.getProductLink()}
        aria-label={this.props.product.name}
        className={classes.join(' ')}
        onClick={this.onClickHandler}
        disabled={this.props.isDisabled}
      >
        { this.renderCallout() }
        { this.renderProductImage() }
        <div className="product-card-content">
          <div className="price">
            <Price prices={this.props.product.prices} />
          </div>
          <div className="product-details">
            <div className="product-name">{ this.props.product.name }</div>
            <div className="producer">{ helpers.formatProducers(this.props.product.producers) }</div>
          </div>
          <div className="details">
            <Rating
              average={this.props.product.avg_rating}
              count={this.props.product.reviews_count}
            />
            { this.props.product['is_plus_subscribable?'] ? <PlusLogo className="is-subscribable" /> : false }
          </div>
        </div>
      </a>
    )
  }
}

DetailedProductCard.defaultProps = {
  product: {},
  position: 0,
  listName: '',
  loading: false,
  isDisabled: false,
}

DetailedProductCard.propTypes = {
  product: PropTypes.object,
  position: PropTypes.number,
  listName: PropTypes.string,
  loading: PropTypes.bool,
  isDisabled: PropTypes.bool,
}

export default DetailedProductCard
